import client from './client'
import type {
  Event,
  Edition,
  Race,
  StoreEventPayload,
  UpdateEventPayload,
  StoreEditionPayload,
  UpdateEditionPayload,
  StoreRacePayload,
  UpdateRacePayload,
  PublicEventData,
} from '@/types/events'

const BASE = '/organizer/events'

export async function listEvents(organisationId: number): Promise<Event[]> {
  const { data } = await client.get<{ data: Event[] }>(
    `/organizer/organisations/${organisationId}/events`,
  )
  return data.data
}

export async function createEvent(organisationId: number, payload: StoreEventPayload): Promise<Event> {
  const { data } = await client.post<{ data: Event }>(
    `/organizer/organisations/${organisationId}/events`,
    payload,
  )
  return data.data
}

export async function getEvent(eventSlug: string): Promise<Event> {
  const { data } = await client.get<{ data: Event }>(`${BASE}/${eventSlug}`)
  return data.data
}

export async function updateEvent(eventSlug: string, payload: UpdateEventPayload): Promise<Event> {
  const { data } = await client.put<{ data: Event }>(`${BASE}/${eventSlug}`, payload)
  return data.data
}

export async function deleteEvent(eventSlug: string): Promise<void> {
  await client.delete(`${BASE}/${eventSlug}`)
}

export async function listEditions(eventSlug: string): Promise<Edition[]> {
  const { data } = await client.get<{ data: Edition[] }>(`${BASE}/${eventSlug}/editions`)
  return data.data
}

export async function createEdition(
  eventSlug: string,
  payload: StoreEditionPayload,
): Promise<Edition> {
  const { data } = await client.post<{ data: Edition }>(
    `${BASE}/${eventSlug}/editions`,
    payload,
  )
  return data.data
}

export async function getEdition(eventSlug: string, editionSlug: string): Promise<Edition> {
  const { data } = await client.get<{ data: Edition }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}`,
  )
  return data.data
}

export async function updateEdition(
  eventSlug: string,
  editionSlug: string,
  payload: UpdateEditionPayload,
): Promise<Edition> {
  const { data } = await client.put<{ data: Edition }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}`,
    payload,
  )
  return data.data
}

export async function activateEdition(eventSlug: string, editionSlug: string): Promise<Edition> {
  const { data } = await client.post<{ data: Edition }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}/activate`,
  )
  return data.data
}

export async function deleteEdition(eventSlug: string, editionSlug: string): Promise<void> {
  await client.delete(`${BASE}/${eventSlug}/editions/${editionSlug}`)
}

export async function listRaces(eventSlug: string, editionSlug: string): Promise<Race[]> {
  const { data } = await client.get<{ data: Race[] }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}/races`,
  )
  return data.data
}

export async function createRace(
  eventSlug: string,
  editionSlug: string,
  payload: StoreRacePayload,
): Promise<Race> {
  const { data } = await client.post<{ data: Race }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}/races`,
    payload,
  )
  return data.data
}

export async function getRace(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
): Promise<Race> {
  const { data } = await client.get<{ data: Race }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}/races/${raceSlug}`,
  )
  return data.data
}

export async function updateRace(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
  payload: UpdateRacePayload,
): Promise<Race> {
  const { data } = await client.put<{ data: Race }>(
    `${BASE}/${eventSlug}/editions/${editionSlug}/races/${raceSlug}`,
    payload,
  )
  return data.data
}

export async function deleteRace(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
): Promise<void> {
  await client.delete(`${BASE}/${eventSlug}/editions/${editionSlug}/races/${raceSlug}`)
}

// Public event races page
export async function fetchPublicEventRaces(orgSlug: string, eventSlug: string): Promise<PublicEventData> {
  const { data } = await client.get<{ data: PublicEventData }>(`/${orgSlug}/${eventSlug}`)
  return data.data
}
